/**
 * useWebSocket Hook
 * Socket.IO connection for real-time chat sync (new messages + delivery-status ticks).
 *
 * Connects once per tenantId and joins the tenant-wide room, which is enough to receive
 * every message event for the tenant. subscribe/unsubscribeFromConversation additionally
 * join/leave a per-conversation room — harmless overlap today (Socket.IO dedupes a single
 * socket's room memberships per emit), and keeps a clean room lifecycle for callers that
 * only care about one open conversation.
 *
 * Handlers are stored in a ref and refreshed on every render, so callers can pass plain
 * inline closures (capturing whatever state is current) without forcing a reconnect —
 * only a change to `tenantId` tears down and recreates the socket.
 */

import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import apiService from '@/services/api.service';

export interface SocketMessage {
  _id: string;
  role: 'user' | 'assistant';
  content: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  whatsappMessageId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  // ── Inbound capture ──────────────────────────────────────────────────────
  // Must stay in step with getConversationDetail's projection: a live-arriving
  // message has to render identically to the same message after a reload.
  // Media arrives with `storageState: 'pending'` and NO urls — the download is
  // still queued — so the client shows a loading state and swaps in the
  // thumbnail when `message:media` lands.
  messageType?: string;
  attachments?: unknown[];
  location?: unknown;
  contacts?: unknown[];
  order?: unknown;
  system?: unknown;
  providerErrors?: unknown[];
}

export interface NewMessageEvent {
  conversationId: string;
  /** Present only when the event arrives from the conversation room (full payload).
   *  Tenant-room broadcasts are stubs with no message content — check before use. */
  message?: SocketMessage;
}

export interface MessageStatusEvent {
  conversationId: string;
  whatsappMessageId: string;
  status: string;
  /** Only present when status is 'failed' — the reason WhatsApp rejected the send. */
  sendError?: string;
  /** Only present on an Instagram unsend notification — replaces the
   *  message's displayed content (e.g. "Message unsent") without changing its
   *  `status`, which is why this rides the same status-patch event rather
   *  than getting its own: an unsend is a content change, not a delivery-
   *  state change, but it needs the identical "find by providerMessageId,
   *  patch in place" mechanism this event already has. */
  content?: string;
}

export interface ConversationUnreadEvent {
  conversationId: string;
  /** Absolute value, never a delta — safe to just overwrite local state with it. */
  unreadCount: number;
}

export interface ConversationRoutingEvent {
  conversationId: string;
  /** Absent when only ownership changed (tag-rule / manual / bulk assign) — the
   *  bot keeps replying, so there is no state transition to report. Mirrors the
   *  backend's ConversationRoutingPayload, where this field is optional too. */
  state?: 'bot' | 'pending_human' | 'human';
  assignedTo?: string;
  assignmentMethod?: 'round_robin' | 'role_round_robin' | 'owner_fallback' | 'claim' | 'manual';
}

/** A reaction was added to / removed from an already-rendered message.
 *  Reactions mutate an EXISTING bubble rather than appending a new one, so they
 *  cannot travel on `message:new` — the target is usually far up the transcript.
 *  `emoji: null` means the reaction was withdrawn. */
export interface MessageReactionEvent {
  conversationId: string;
  whatsappMessageId: string;
  by: string;
  emoji: string | null;
}

/** An attachment finished downloading (or terminally failed) AFTER its message
 *  was already rendered. Media is captured before the bytes are fetched — the
 *  webhook cannot wait on a 16MB file — so the bubble first appears in a
 *  loading state and this event resolves it. */
export interface MessageMediaEvent {
  conversationId: string;
  messageId: string;
  attachmentIndex: number;
  storageState: 'pending' | 'stored' | 'expired' | 'skipped_quota' | 'failed' | 'deleted';
  thumbUrl?: string;
  s3Url?: string;
  size?: number;
  mimeType?: string;
  width?: number;
  height?: number;
  error?: string;
  /** Server's verdict on whether this attachment may be displayed at all.
   *  Never inferred client-side from a missing thumbUrl: video, voice notes and
   *  documents never carry a thumbnail, so that inference locks them forever. */
  quotaLocked?: boolean;
}

/**
 * The tenant's inbound-media meter moved — a capture stored (or refused) bytes,
 * or a delete/sweep/retention run freed them.
 *
 * This is the only signal a client gets for a quota that moves without any
 * client action: media arrives from a webhook, and a delete in one tab is
 * invisible to a chat transcript open in another. `overQuota` is the same
 * boolean the server's render gate uses, so a page can lock/unlock previews in
 * the same tick the server would.
 */
export interface MediaQuotaEvent {
  tenantId: string;
  used: number;
  limit: number;
  overQuota: boolean;
  /** Cumulative bytes refused for want of space and not yet recovered — the
   *  number that explains why `used` did not move when a file bounced. */
  skippedBytes: number;
  skippedCount: number;
}

/** The rescore queue wrote new score/priority values. `refresh` = too many to list — re-read. */
export interface LeadScoredEvent {
  leads?: Array<{ leadId: string; score: number; priority: string }>;
  refresh?: boolean;
}

interface WebSocketHandlers {
  onLeadScored?: (data: LeadScoredEvent) => void;
  onNewMessage?: (data: NewMessageEvent) => void;
  onMessageReaction?: (data: MessageReactionEvent) => void;
  onMessageMedia?: (data: MessageMediaEvent) => void;
  onMediaQuota?: (data: MediaQuotaEvent) => void;
  onMessageStatus?: (data: MessageStatusEvent) => void;
  onConversationUnread?: (data: ConversationUnreadEvent) => void;
  /** Fired whenever routing.state or assignedTo changes on a conversation —
   *  lets the list patch the row in-place without a full refetch. */
  onConversationRouting?: (data: ConversationRoutingEvent) => void;
  /** System alerts were raised / re-opened / acknowledged / resolved. No payload — refetch. */
  onAlertsChanged?: () => void;
  /** Something the sidebar Activity badges count arrived (enquiry / capture / comment). No payload — refetch. */
  onActivityChanged?: () => void;
  /**
   * The open conversation may have missed live messages: one arrived before the
   * server confirmed the subscription (its access check takes a moment), or the
   * socket (re)connected after the transcript was loaded. Re-sync the tail —
   * merge, don't replace. Fires at most once per subscription.
   */
  onSubscriptionGap?: (conversationId: string) => void;
}

export const useWebSocket = (tenantId?: string, handlers: WebSocketHandlers = {}) => {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // Tracks whichever conversation the page last asked to subscribe to, so a
  // silent reconnect (sleep/wake, network blip, dev-server restart) can
  // rejoin that room too — without this, `connect` only re-runs `join:tenant`
  // and an already-open thread stops receiving live message content until
  // something else forces a refetch (see project-instagram-integration memory,
  // the "screen froze" report: tenant-room stubs kept bumping the list while
  // the open transcript silently stalled).
  const activeConversationIdRef = useRef<string | null>(null);
  // Live content for a conversation only flows once the server has joined the
  // socket to its room (an access check — a few DB reads). Until the ack, a
  // tenant-room "new message" stub for the open conversation means its content
  // may have been missed; on ack we ask the page to re-sync just that tail.
  const ackedConversationRef = useRef<string | null>(null);
  const gapRef = useRef(false);

  const emitSubscribe = (socket: Socket, conversationId: string, assumeGap: boolean) => {
    ackedConversationRef.current = null;
    gapRef.current = assumeGap;
    socket.emit('subscribe:conversation', { conversationId }, (res?: { ok?: boolean }) => {
      if (activeConversationIdRef.current !== conversationId) return; // switched away meanwhile
      ackedConversationRef.current = conversationId;
      const missed = gapRef.current;
      gapRef.current = false;
      if (res?.ok && missed) handlersRef.current.onSubscriptionGap?.(conversationId);
    });
  };

  useEffect(() => {
    if (!tenantId) {
      console.warn('[useWebSocket] No tenantId provided — skipping connection. Real-time updates are off.');
      return;
    }

    const socketUrl = import.meta.env.VITE_WS_URL || 'http://localhost:5000';
    console.log(`[useWebSocket] Connecting to ${socketUrl} for tenant ${tenantId}`);
    const socket = io(socketUrl, {
      // A function, not a value: read on EVERY (re)connect attempt, so a
      // reconnect after a network blip carries the current — refreshed — token
      // rather than the one from page load (expired after 15 minutes).
      auth: (cb) => cb({ token: localStorage.getItem('admin_token') }),
    });
    socketRef.current = socket;

    // The server refuses the handshake with a code (see backend socket-auth.ts).
    // Socket.IO does NOT retry a refused handshake on its own, so:
    //  - TOKEN_EXPIRED → refresh (same single-flight refresh as HTTP) and retry;
    //    bounded, so a clock-skewed token can't loop forever;
    //  - SESSION_REVOKED → this session is over: the normal logout;
    //  - UNAVAILABLE → the server couldn't check (e.g. a DB blip): retry with backoff;
    //  - anything else (no/invalid token, disabled user) → stay disconnected;
    //    the next HTTP call takes the app through its own 401 handling.
    let expiredRetries = 0;
    let unavailableRetries = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    socket.on('connect_error', (err) => {
      console.error('[useWebSocket] ❌ Connection error:', err.message);
      if (err.message === 'SESSION_REVOKED') {
        apiService.forceLogout();
        return;
      }
      if (err.message === 'UNAVAILABLE') {
        const delay = Math.min(30_000, 1000 * 2 ** unavailableRetries++);
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => {
          if (socketRef.current === socket) socket.connect();
        }, delay);
        return;
      }
      if (err.message !== 'TOKEN_EXPIRED' || expiredRetries >= 2) return;
      expiredRetries += 1;
      apiService
        .refreshSession()
        .then((token) => {
          if (token && socketRef.current === socket) socket.connect();
        })
        .catch(() => apiService.forceLogout()); // refresh refused → session is dead
    });

    socket.on('connect', () => {
      console.log(`[useWebSocket] ✅ Connected (id=${socket.id}, transport=${socket.io.engine.transport.name})`);
      expiredRetries = 0;
      unavailableRetries = 0;
      setConnected(true);
      // The server already put this socket in its own tenant's room at the
      // handshake; this emit is kept for older servers and is ignored otherwise.
      socket.emit('join:tenant', { tenantId });
      // (Re)subscribe the open conversation. Assume a gap: whatever arrived
      // while this socket was down (or before it first connected, after the
      // transcript was loaded) never reached the open chat.
      if (activeConversationIdRef.current) {
        emitSubscribe(socket, activeConversationIdRef.current, true);
      }
    });

    // Session revoked / user disabled while connected: the server says so, then
    // closes the socket (and won't take it back) — end the session here too.
    socket.on('auth:revoked', () => {
      console.warn('[useWebSocket] Session ended by the server');
      apiService.forceLogout();
    });

    socket.on('disconnect', (reason) => {
      console.log(`[useWebSocket] ❌ Disconnected (${reason})`);
      setConnected(false);
    });

    socket.on('message:new', (data: NewMessageEvent) => {
      // A content-less stub for the open conversation before its subscription
      // is confirmed: the full message went to a room this socket isn't in yet.
      if (
        !data.message &&
        data.conversationId === activeConversationIdRef.current &&
        ackedConversationRef.current !== data.conversationId
      ) {
        gapRef.current = true;
      }
      handlersRef.current.onNewMessage?.(data);
    });

    socket.on('message:media', (data: MessageMediaEvent) => {
      handlersRef.current.onMessageMedia?.(data);
    });

    socket.on('media:quota', (data: MediaQuotaEvent) => {
      handlersRef.current.onMediaQuota?.(data);
    });

    socket.on('message:reaction', (data: MessageReactionEvent) => {
      handlersRef.current.onMessageReaction?.(data);
    });

    socket.on('message:status', (data: MessageStatusEvent) => {
      handlersRef.current.onMessageStatus?.(data);
    });

    socket.on('conversation:unread', (data: ConversationUnreadEvent) => {
      handlersRef.current.onConversationUnread?.(data);
    });

    socket.on('conversation:routing', (data: ConversationRoutingEvent) => {
      handlersRef.current.onConversationRouting?.(data);
    });

    socket.on('lead:scored', (data: LeadScoredEvent) => {
      handlersRef.current.onLeadScored?.(data);
    });

    socket.on('activity:changed', () => {
      handlersRef.current.onActivityChanged?.();
    });

    socket.on('alerts:changed', () => {
      handlersRef.current.onAlertsChanged?.();
    });

    return () => {
      clearTimeout(retryTimer);
      socket.close();
      socketRef.current = null;
    };
    // Reconnecting is only ever needed when the tenant changes (e.g. logout/login as
    // someone else) — handlers are read fresh from the ref above, not from this closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const subscribeToConversation = (conversationId: string) => {
    activeConversationIdRef.current = conversationId;
    // Not connected yet → the 'connect' handler subscribes it (and re-syncs).
    const socket = socketRef.current;
    if (socket?.connected) emitSubscribe(socket, conversationId, false);
  };

  const unsubscribeFromConversation = (conversationId: string) => {
    if (activeConversationIdRef.current === conversationId) {
      activeConversationIdRef.current = null;
      ackedConversationRef.current = null;
      gapRef.current = false;
    }
    socketRef.current?.emit('unsubscribe:conversation', { conversationId });
  };

  return {
    connected,
    subscribeToConversation,
    unsubscribeFromConversation,
  };
};
